import React, { useEffect, useState } from 'react'
import { Container, TabContent, TabPane, Alert } from 'reactstrap'
import { useQuery } from '@apollo/client'
import { useParams, useNavigate } from 'react-router-dom'
import { IS_INBOX_ADMIN } from './Queries'
import InboxManagement from './InboxManagement'
import ChannelManagement from './ChannelManagement'
import InboxMemberManagement from './InboxMemberManagement'
import MessageTemplateManagement from './MessageTemplateManagement'
import ReportsPanel from '../../Reports/ReportsPanel'
import ListingTabs from '../../Material/ListingTabs'
import './CommsAdmin.css'

const TABS = {
    INBOXES: 'inboxes',
    CHANNELS: 'channels',
    MEMBERS: 'members',
    TEMPLATES: 'templates',
    ACTIVITY: 'activity',
}

// The Activity Log tab is floated to the right, icon-only -- the same
// convention every other listing page in this app uses for its own Activity
// Log tab (see IncidentsListContainer.js, CustomerLeads/LeadsListContainer.js,
// PriceManagement/PricingPromotionsPage.js, Maintenance/MaintenancePage.js),
// via the same shared `ListingTabs` component and its `floatRight` /
// `iconOnly` / `iconClassName` tab config, rather than a bespoke NavLink
// styled to merely look similar. `ListingTabs` sets both `title` and
// `aria-label` from `name` unconditionally, so hiding the visible label with
// `iconOnly` still leaves the tab with an accessible name for screen readers.
const NAV_TABS = [
    { id: TABS.INBOXES, name: 'Inboxes', iconClassName: 'fa fa-inbox' },
    { id: TABS.CHANNELS, name: 'Channels', iconClassName: 'fa fa-comments' },
    { id: TABS.MEMBERS, name: 'Members', iconClassName: 'fa fa-users' },
    { id: TABS.TEMPLATES, name: 'Templates', iconClassName: 'fa fa-sticky-note' },
    { id: TABS.ACTIVITY, name: 'Activity Log', iconClassName: 'fa fa-history', floatRight: true, iconOnly: true },
]

export default function CommsAdminContainer() {
    const { tabName } = useParams()
    const navigate = useNavigate()
    const sanitizedTabName = Object.values(TABS).includes(tabName) ? tabName : TABS.INBOXES
    const [activeTab, setActiveTab] = useState(sanitizedTabName)

    const { data, loading, error } = useQuery(IS_INBOX_ADMIN, {
        fetchPolicy: 'network-only'
    })

    const isAdmin = data?.isInboxAdmin

    useEffect(() => {
        if (sanitizedTabName !== activeTab) {
            setActiveTab(sanitizedTabName)
        }
    }, [sanitizedTabName, activeTab])

    const handleTabChange = (tab) => {
        if (tab === activeTab) {
            return
        }
        setActiveTab(tab)
        if (tab === TABS.INBOXES) {
            navigate('/comms/admin')
        } else {
            navigate(`/comms/admin/${tab}`)
        }
    }


    if (loading) {
        return (
            <Container fluid className="comms-admin-container">
                <div className="comms-admin-hero loading">
                    <div className="comms-admin-hero-copy">
                        <span className="skeleton-block skeleton-chip" />
                        <span className="skeleton-block skeleton-title" />
                        <span className="skeleton-block skeleton-text" />
                    </div>
                </div>

                <div className="comms-admin-card">
                    <div className="comms-admin-tabs loading">
                        {[0, 1, 2].map((index) => (
                            <span key={index} className="skeleton-block skeleton-pill" />
                        ))}
                    </div>
                    <div className="comms-admin-tab-content loading">
                        {[0, 1, 2, 3].map((index) => (
                            <div key={index} className="skeleton-block skeleton-row" />
                        ))}
                    </div>
                </div>
            </Container>
        )
    }

    if (error) {
        return (
            <Container fluid className="comms-admin-container">
                <Alert color="danger">
                    Error loading admin panel: {error.message}
                </Alert>
            </Container>
        )
    }

    if (!isAdmin) {
        return (
            <Container fluid className="comms-admin-container">
                <Alert color="warning">
                    <h4 className="alert-heading">Access Denied</h4>
                    <p>
                        You do not have admin permissions for any inbox. 
                        Please contact an administrator to grant you admin access.
                    </p>
                </Alert>
            </Container>
        )
    }

    return (
        <Container fluid className="comms-admin-container">
            <ListingTabs tabs={NAV_TABS} activeTab={activeTab} onChange={handleTabChange} />
            <div className="comms-admin-card">
                <TabContent activeTab={activeTab} className="comms-admin-tab-content">
                    <TabPane tabId={TABS.INBOXES}>
                        {activeTab === TABS.INBOXES && <InboxManagement />}
                    </TabPane>
                    <TabPane tabId={TABS.CHANNELS}>
                        {activeTab === TABS.CHANNELS && <ChannelManagement />}
                    </TabPane>
                    <TabPane tabId={TABS.MEMBERS}>
                        {activeTab === TABS.MEMBERS && <InboxMemberManagement />}
                    </TabPane>
                    <TabPane tabId={TABS.TEMPLATES}>
                        {activeTab === TABS.TEMPLATES && <MessageTemplateManagement />}
                    </TabPane>
                    <TabPane tabId={TABS.ACTIVITY}>
                        {activeTab === TABS.ACTIVITY && <ReportsPanel reportId="inbox" />}
                    </TabPane>
                </TabContent>
            </div>
        </Container>
    )
}
